/**
 * MobileCarousel - Fullscreen carousel tour for mobile
 * PRD: PRD-product-tour-organisateur.md
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Icon } from '../../../pages/DesignSystem';
import { mobileSlides } from './tourSteps';
import './ProductTour.css';

interface MobileCarouselProps {
  isOpen: boolean;
  onComplete: () => Promise<void>;
  onSkip: (stepIndex?: number) => Promise<void>;
}

/**
 * MobileCarousel - Fullscreen slideshow tour for mobile organizers
 */
const MobileCarousel: React.FC<MobileCarouselProps> = ({
  isOpen,
  onComplete,
  onSkip,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const totalSlides = mobileSlides.length;
  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide === totalSlides - 1;

  // Reset to first slide when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
    }
  }, [isOpen]);

  // Handle next slide
  const handleNext = useCallback(() => {
    if (isLastSlide) {
      onComplete();
    } else {
      setCurrentSlide((prev) => prev + 1);
    }
  }, [isLastSlide, onComplete]);

  // Handle previous slide
  const handlePrev = useCallback(() => {
    if (!isFirstSlide) {
      setCurrentSlide((prev) => prev - 1);
    }
  }, [isFirstSlide]);

  // Handle skip - pass current slide index for analytics
  const handleSkip = useCallback(() => {
    onSkip(currentSlide);
  }, [onSkip, currentSlide]);

  // Touch handlers for swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === null || touchEndX.current === null) return;

    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        // Swipe left - next
        handleNext();
      } else {
        // Swipe right - prev
        handlePrev();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [handleNext, handlePrev]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'Escape') {
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleNext, handlePrev, handleSkip]);

  if (!isOpen) return null;

  const slide = mobileSlides[currentSlide];

  return (
    <div
      className="mobile-carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Product tour"
    >
      {/* Header with Skip */}
      <div className="mobile-carousel__header">
        <button
          className="mobile-carousel__skip"
          onClick={handleSkip}
          aria-label="Skip tour"
        >
          Skip
        </button>
      </div>

      {/* Slide Content */}
      <div className="mobile-carousel__content">
        <div className="mobile-carousel__slide" key={currentSlide}>
          {/* Icon */}
          <div className="mobile-carousel__icon-wrapper">
            <Icon name={slide.icon as any} size={48} />
          </div>

          {/* Title */}
          <h2 className="mobile-carousel__title">{slide.title}</h2>

          {/* Description */}
          <div className="mobile-carousel__description">{slide.description}</div>
        </div>
      </div>

      {/* Dots indicator */}
      <div className="mobile-carousel__dots" role="tablist">
        {mobileSlides.map((_, index) => (
          <span
            key={index}
            className={`mobile-carousel__dot ${
              index === currentSlide ? 'mobile-carousel__dot--active' : ''
            } ${index < currentSlide ? 'mobile-carousel__dot--completed' : ''}`}
            role="tab"
            aria-selected={index === currentSlide}
            aria-label={`Slide ${index + 1} of ${totalSlides}`}
          />
        ))}
      </div>

      {/* Footer with navigation buttons */}
      <div className="mobile-carousel__footer">
        <div
          className={`mobile-carousel__actions ${
            isFirstSlide || isLastSlide ? 'mobile-carousel__actions--single' : ''
          }`}
        >
          {/* Back button (not on first slide) */}
          {!isFirstSlide && !isLastSlide && (
            <button
              className="mobile-carousel__button mobile-carousel__button--secondary"
              onClick={handlePrev}
              aria-label="Previous slide"
            >
              <Icon name="ChevronLeft" size={18} />
              Back
            </button>
          )}

          {/* Next/Get Started button */}
          <button
            className="mobile-carousel__button mobile-carousel__button--primary"
            onClick={handleNext}
            aria-label={isLastSlide ? 'Get started' : 'Next slide'}
          >
            {isLastSlide ? (
              <>
                Get Started!
                <Icon name="ArrowRight" size={18} />
              </>
            ) : (
              <>
                Next
                <Icon name="ArrowRight" size={18} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileCarousel;
